<template>
    <div v-if="!loaded || !noData">
        <div v-if="loaded">
            <div class="text-h6 text--secondary pb-2 ml-5">
                {{ $t('flights.select_seat') }}
            </div>
            <v-expansion-panels v-model="opennedPanels" variant="accordion" class="mb-5">
                <v-expansion-panel
                    v-for="(seatsCabinClasses, rph) in flightSeatsMap"
                    :key="rph"
                    @group:selected="toggleSegment(rph, $event)"
                >
                    <v-expansion-panel-title v-slot="{expanded}">
                        <div class="d-flex flex-wrap">
                            {{ segment(rph).departure.airportDescription.airportName }},
                            {{ segment(rph).departure.airportDescription.cityName }}
                            ({{ segment(rph).departure.airportCode }})&nbsp;-&nbsp;
                            {{ segment(rph).arrival.airportDescription.airportName }},
                            {{ segment(rph).arrival.airportDescription.cityName }}
                            ({{ segment(rph).arrival.airportCode }})
                            <template v-if="segment(rph).aircraft && segment(rph).aircraft.name">
                                , {{ segment(rph).aircraft.name }}
                            </template>
                        </div>
                        <div class="d-flex flex-column justify-center align-center mr-5">
                            <template v-if="segmentSeats(rph).length">
                                <v-chip
                                    v-for="(seat, index) in segmentSeats(rph)"
                                    :key="index"
                                    size="small"
                                    :disabled="disabled"
                                    label
                                    class="ma-1 text-capitalize"
                                    color="info"
                                    closable
                                    @click:close="unselectSeat(seat)"
                                >
                                    <span
                                        v-if="
                                            tourists[seat.touristIndex].prefix && tourists[seat.touristIndex].lastName
                                        "
                                    >
                                        {{ tourists[seat.touristIndex].prefix }}
                                        {{ tourists[seat.touristIndex].lastName }}:
                                    </span>
                                    <span v-else>{{ $t('passenger', 1) }} {{ seat.touristIndex + 1 }}:</span>
                                    <span class="ms-1">
                                        {{ seat.rowNumber }}{{ seat.seatNumber }}
                                        <span v-if="seatPrice(seat)"> (+{{ $filters.price(seatPrice(seat)) }})</span>
                                    </span>
                                </v-chip>
                            </template>
                            <span v-else-if="expanded">
                                {{ $t('flights.select_seat') }}
                            </span>
                        </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-item-group
                            v-if="loaded"
                            v-model="selected[rph]"
                            multiple
                            :max="selectableSeatsCount"
                            @update:model-value="selectSeat"
                        >
                            <div class="d-flex flex-column justify-center mx-2 mt-2">
                                <v-item v-for="tariff in Object.keys(seatColorMap)" :key="tariff">
                                    <v-card
                                        v-if="seatColorMap[tariff].flight.indexOf(rph) > -1"
                                        :color="seatColorMap[tariff].color"
                                        class="d-flex align-center justify-center seat-number mx-1"
                                        :height="maxSeatWidth"
                                    >
                                        <div class="text-caption">
                                            {{ tariff }}
                                        </div>
                                    </v-card>
                                </v-item>
                            </div>
                            <div
                                v-for="seatsCabinClass in seatsCabinClasses"
                                :key="seatsCabinClass.bookingClass"
                                class="seats-cabin-class"
                                :style="{width: seatsCabinWidth(seatsCabinClasses)}"
                            >
                                <div class="text--h6 text-center pt-5">
                                    {{ seatsCabinClass.cabinClass }}
                                </div>
                                <div
                                    v-for="seatRow in seatsCabinClass.seatRows"
                                    :key="seatRow.rowNumber"
                                    class="my-3"
                                    :style="{height: maxSeatWidth + 'px'}"
                                >
                                    <div class="row-number" :style="{height: maxSeatWidth + 'px'}">
                                        {{ seatRow.rowNumber }}
                                    </div>
                                    <div class="d-flex justify-space-between">
                                        <div
                                            v-for="(seatGroup, seatGroupIndex) in seatRow.seatGroups"
                                            :key="seatGroupIndex"
                                            class="d-flex"
                                            :style="{width: seatGroupWidth(seatRow.seatGroups.length)}"
                                        >
                                            <template v-for="seat in seatGroup" :key="seat.seatNumber">
                                                <v-item
                                                    v-if="seat.seatAvailability"
                                                    v-slot="{isSelected, toggle}"
                                                    :value="`${rph}_${seatRow.rowNumber}_${seat.seatNumber}`"
                                                >
                                                    <v-tooltip location="bottom">
                                                        <template v-slot:activator="{props}">
                                                            <v-card
                                                                :ref="`seatNumber-${rph}`"
                                                                :disabled="disabled"
                                                                :color="isSelected ? 'primary' : makeSeatMapColor(seat)"
                                                                class="d-flex align-center justify-center seat-number mx-1"
                                                                :height="maxSeatWidth"
                                                                :width="seatWidth(seatGroup.length)"
                                                                v-bind="props"
                                                                @click="clickSeat(toggle, $event, rph)"
                                                            >
                                                                <div class="text-caption">
                                                                    {{ seat.seatNumber }}
                                                                </div>
                                                            </v-card>
                                                        </template>
                                                        <span>{{ createNameForColorMap(seat.passengerFares) }}</span>
                                                    </v-tooltip>
                                                </v-item>
                                                <v-card
                                                    v-else
                                                    :key="seat.seatNumber"
                                                    ref="seatNumber"
                                                    color="grey-lighten-3"
                                                    class="d-flex align-center justify-center seat-number mx-1"
                                                    :height="maxSeatWidth"
                                                    :width="seatWidth(seatGroup.length)"
                                                    variant="outlined"
                                                >
                                                    <div class="text-caption">
                                                        {{ seat.seatNumber }}
                                                    </div>
                                                </v-card>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-item-group>
                        <v-container v-else class="loader">
                            <v-skeleton-loader type="table-row@16" :elevation="0" />
                        </v-container>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div v-else>
            <span class="text-h6 text--secondary pb-2 ml-5">
                {{ $t('loadind_seats') }}
            </span>
            <v-skeleton-loader :type="`list-item@${segmentRphs.length}`" :elevation="0" class="mb-5" />
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {clone, isEqual} from '@/utils/helpers'

    @Component
    export class FlightSeatsMap extends Vue {
        @VModel() prepareBookRequest
        @Prop({required: true}) offer
        @Prop({required: true}) searchRequest
        @Prop({default: false, type: Boolean}) disabled
        @Prop({required: true}) tourists

        selected = {}
        maxSeatWidth = 25
        loaded = false
        segmentRphs = []
        seatColorMap = {}
        opennedPanels = false
        flightSeatsMapData = {}

        @Emit()
        mapLoaded() {
            return this.flightSeatsMapData
        }

        @Watch('disabled')
        closeAllExpandPanels(n) {
            if (n) {
                this.opennedPanels = false
            }
        }

        async mounted() {
            const {offerKey} = this.offer
            this.segmentRphs = this.offer.itinerary.reduce((segmentRphs, {segments}) => {
                segments.forEach(({rph}) => {
                    segmentRphs.push(rph)
                    let selectedValues = []
                    if (this.prepareBookRequest.seats && this.prepareBookRequest.offerKey === offerKey) {
                        selectedValues = this.prepareBookRequest.seats
                            .filter(seat => seat.segmentRPH === rph)
                            .map(seat => `${rph}_${seat.rowNumber}_${seat.seatNumber}`)
                    } else if (this.prepareBookRequest.offerKey !== offerKey) {
                        this.prepareBookRequest.seats = []
                    }
                    this.selected[rph] = selectedValues
                })
                return segmentRphs
            }, [])
            await this.loadFlightSeatsMap({
                offerKey,
                segmentRphs: this.segmentRphs,
            })
            this.makeSeatColorMap(this.flightSeatsMapData)
            this.loaded = true
        }

        async loadFlightSeatsMap({offerKey, segmentRphs}) {
            try {
                const promises = segmentRphs.map(segmentRph => this.$api.flightSeatsMap.get({offerKey, segmentRph}))
                const result = await Promise.all(promises)
                this.flightSeatsMapData = result.reduce((flightSeatsMap, {seatsCabinClasses}, index) => {
                    if (seatsCabinClasses) {
                        flightSeatsMap[segmentRphs[index]] = seatsCabinClasses
                    }
                    return flightSeatsMap
                }, {})
            } catch (e) {
                this.flightSeatsMapData = {}
            } finally {
                this.mapLoaded()
            }
        }

        makeSeatColorMap(map) {
            const keys = Object.keys(map)
            Object.values(map).forEach((init, index) => {
                init.forEach(cabin => {
                    if (cabin.seatRows.length) {
                        cabin.seatRows.forEach(row => {
                            if (row.seats.length) {
                                row.seats.forEach(seat => {
                                    if (seat.passengerFares.length) {
                                        let name = this.createNameForColorMap(seat.passengerFares)
                                        if (!this.seatColorMap[name]) {
                                            this.seatColorMap[name] = {
                                                color: this.getRandomColor(),
                                                flight: [keys[index]],
                                            }
                                        } else {
                                            if (this.seatColorMap[name].flight.indexOf(keys[index]) === -1) {
                                                this.seatColorMap[name].flight.push(keys[index])
                                            }
                                        }
                                    }
                                })
                            }
                        })
                    }
                })
            })
        }

        createNameForColorMap(tariffs) {
            let name = ''
            if (tariffs.length) {
                tariffs.forEach(tariff => {
                    if (name.length) {
                        name += '\\ '
                    }
                    if (tariff.passengerType) {
                        name += `${tariff.passengerType} - `
                    }
                    name += `${tariff.price.amount} ${tariff.price.currency}`
                })
            }
            return name ? name : this.$t('no_tariff')
        }

        segment(rph) {
            let segmentIndex
            const itineraryIndex = this.offer.itinerary.findIndex(({segments}) => {
                const index = segments.findIndex(segment => segment.rph === rph)
                if (index !== -1) {
                    segmentIndex = index
                    return true
                } else {
                    return false
                }
            })
            return this.offer.itinerary[itineraryIndex].segments[segmentIndex]
        }

        segmentSeats(rph) {
            return this.seats.filter(seat => seat.segmentRPH === rph)
        }

        selectSeat() {
            this.prepareBookRequest.seats = this.seats
            if (this.prepareBookRequest.offerKey !== this.offer.offerKey) {
                this.prepareBookRequest.offerKey = this.offer.offerKey
            }
        }

        unselectSeat(seat) {
            this.selected[seat.segmentRPH] = this.selected[seat.segmentRPH].filter(
                key => key !== `${seat.segmentRPH}_${seat.rowNumber}_${seat.seatNumber}`
            )
            const seats = this.seats.filter(seatItem => !isEqual(seatItem, seat))
            this.prepareBookRequest.seats = seats
        }

        seatGroupWidth(count) {
            return (100 - (count - 1) * 10) / count + '%'
        }

        seatWidth(count) {
            return (100 - (count - 1) * 2) / count + '%'
        }

        seatsCabinWidth(seatsCabinClasses) {
            const maxRowSeatsCount = seatsCabinClasses.reduce((maxRowSeatsCount, seatsCabinClass) => {
                seatsCabinClass.seatRows.forEach(seatRow => {
                    const rowSeatsCount = seatRow.seatGroups.reduce((rowSeatsCount, seatGroup) => {
                        rowSeatsCount += seatGroup.length
                        return rowSeatsCount
                    }, 0)
                    if (rowSeatsCount > maxRowSeatsCount) {
                        maxRowSeatsCount = rowSeatsCount
                    }
                })
                return maxRowSeatsCount
            }, 0)
            return (maxRowSeatsCount <= 4 ? 200 : 400) + 'px'
        }

        toggleSegment(rph, show) {
            if (show) {
                this.maxSeatWidth = Math.max(
                    ...this.$refs[`seatNumber-${rph}`].map(seatNumber => seatNumber.$el.clientWidth)
                )
            }
        }

        seatPrice({segmentRPH, rowNumber, seatNumber, touristIndex}) {
            let price
            this.flightSeatsMapData[segmentRPH].forEach(seatsCabinClass => {
                const seatRow = seatsCabinClass.seatRows.find(seatRow => {
                    return seatRow.rowNumber === +rowNumber
                })
                if (seatRow) {
                    const seat = seatRow.seats.find(seat => seat.seatNumber === seatNumber)
                    if (seat) {
                        const passengerType = this.tourists[touristIndex].type === 'adult' ? 'ADT' : 'CHD'
                        const passengerFare = seat.passengerFares.find(
                            passengerFare => passengerFare.passengerType === passengerType
                        )
                        if (passengerFare) {
                            price = passengerFare.price
                        }
                    }
                }
            })
            return price
        }

        async clickSeat(toggle, $event, rph) {
            if (this.disabled) return
            if (this.segmentSeats(rph).length === this.selectableSeatsCount) {
                this.selected[rph] = []
                await this.$nextTick()
            }
            toggle($event)
        }

        makeSeatMapColor(seat) {
            if (seat.passengerFares.length) {
                return this.seatColorMap[this.createNameForColorMap(seat.passengerFares)].color
            }
            return ''
        }

        getRandomColor() {
            const randomBetween = (min, max) => min + Math.floor(Math.random() * (max - min + 1))
            const r = randomBetween(120, 255)
            const g = randomBetween(120, 255)
            const b = randomBetween(120, 255)
            const rgb = `rgba(${r},${g},${b}, 0.3)`
            return rgb
        }

        get seats() {
            return Object.values(this.selected).reduce((seats, selectedValues) => {
                selectedValues.forEach((selectedValue, touristIndex) => {
                    const data = selectedValue.split('_')
                    seats.push({
                        segmentRPH: data[0],
                        touristIndex,
                        rowNumber: data[1],
                        seatNumber: data[2],
                    })
                })
                return seats
            }, [])
        }

        get flightSeatsMap() {
            const flightSeatsMap = clone(this.flightSeatsMapData)
            const noAvailableSeatsSegments = []
            Object.values(flightSeatsMap).forEach((segmentSeatsMap, index) => {
                let noAvailableSeats = true
                segmentSeatsMap.forEach(seatsCabinClasses =>
                    seatsCabinClasses.seatRows.forEach(seatRow => {
                        let seatGroupIndex = 0,
                            nextGroup = false
                        seatRow.seatGroups = seatRow.seats.reduce(
                            (seatGroups, seat) => {
                                if (seat.seatCharDescription.includes('Aisle')) {
                                    if (nextGroup) {
                                        seatGroupIndex++
                                        nextGroup = false
                                    } else {
                                        seatGroups[seatGroupIndex + 1] = []
                                        nextGroup = true
                                    }
                                }
                                seatGroups[seatGroupIndex].push(seat)
                                if (seat.seatAvailability) noAvailableSeats = false
                                return seatGroups
                            },
                            [[]]
                        )
                    })
                )
                if (noAvailableSeats) noAvailableSeatsSegments.push(Object.keys(flightSeatsMap)[index])
            })
            noAvailableSeatsSegments.forEach(rph => {
                delete flightSeatsMap[rph]
            })
            return flightSeatsMap
        }

        get selectableSeatsCount() {
            return this.searchRequest.adults + this.searchRequest.childrenAges.filter(age => age >= 2).length
        }

        get noData() {
            return Object.values(this.flightSeatsMap).every(segmentSeatsMap => !segmentSeatsMap.length)
        }
    }

    export default FlightSeatsMap
</script>

<style scoped lang="scss">
    .seats-cabin-class {
        position: relative;
        max-width: 400px;
        margin: 0 auto;
    }
    .row-number {
        position: absolute;
        left: -30px;
        align-items: center;
        display: flex;
    }
    .seat-number {
        &.v-item--active {
            color: white;
        }
    }
    .loader {
        width: 200px;
    }
</style>
